import { useState } from 'react';
import { Box, Stack, Group, Badge, TextInput, Select, Table } from '@mantine/core';
import {
  IconSearch,
  IconCreditCard,
  IconBuildingBank,
  IconBrandPaypal,
  IconCash,
} from '@tabler/icons-react';
import { Card, Text, Title } from '../../../../components/ui';
import { mockPayments, paymentsByMethod, Payment } from '../mockData/invoicing';

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
};

const formatDateTime = (date: Date) => {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
};

const getStatusColor = (status: Payment['status']) => {
  switch (status) {
    case 'completed': return 'green';
    case 'pending': return 'yellow';
    case 'failed': return 'red';
    case 'refunded': return 'orange';
    default: return 'gray';
  }
};

const getMethodIcon = (method: Payment['method']) => {
  switch (method) {
    case 'credit_card': return IconCreditCard;
    case 'bank_transfer': return IconBuildingBank;
    case 'paypal': return IconBrandPaypal;
    case 'cash': return IconCash;
    case 'check': return IconCash;
    default: return IconCash;
  }
};

const getMethodLabel = (method: Payment['method']) => {
  switch (method) {
    case 'credit_card': return 'Credit Card';
    case 'bank_transfer': return 'Bank Transfer';
    case 'paypal': return 'PayPal';
    case 'cash': return 'Cash';
    case 'check': return 'Check';
    default: return method;
  }
};

export default function PaymentsPage() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [methodFilter, setMethodFilter] = useState<string | null>(null);

  const statuses = ['completed', 'pending', 'failed', 'refunded'];
  const methods = ['credit_card', 'bank_transfer', 'paypal', 'check', 'cash'];

  const filteredPayments = mockPayments.filter(payment => {
    const matchesSearch = payment.invoiceNumber.toLowerCase().includes(search.toLowerCase()) ||
                          payment.clientName.toLowerCase().includes(search.toLowerCase()) ||
                          payment.transactionId?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = !statusFilter || payment.status === statusFilter;
    const matchesMethod = !methodFilter || payment.method === methodFilter;
    return matchesSearch && matchesStatus && matchesMethod;
  });

  // Sort by date descending
  const sortedPayments = [...filteredPayments].sort((a, b) => b.processedAt.getTime() - a.processedAt.getTime());

  const completedTotal = mockPayments.filter(p => p.status === 'completed').reduce((acc, p) => acc + p.amount, 0);
  const pendingTotal = mockPayments.filter(p => p.status === 'pending').reduce((acc, p) => acc + p.amount, 0);

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group justify="space-between">
        <Box>
          <Title order={2}>Payments</Title>
          <Text c="dimmed" size="sm">{filteredPayments.length} payments</Text>
        </Box>
      </Group>

      {/* Summary Cards */}
      <Group>
        <Card style={{ flex: 1 }}>
          <Text size="xs" c="dimmed">Total Received</Text>
          <Text size="xl" fw={700} c="green">{formatCurrency(completedTotal)}</Text>
        </Card>
        <Card style={{ flex: 1 }}>
          <Text size="xs" c="dimmed">Pending</Text>
          <Text size="xl" fw={700} c="yellow">{formatCurrency(pendingTotal)}</Text>
        </Card>
        {paymentsByMethod.filter(m => m.count > 0).slice(0, 2).map(method => (
          <Card key={method.method} style={{ flex: 1 }}>
            <Text size="xs" c="dimmed">{method.method}</Text>
            <Text size="xl" fw={700}>{formatCurrency(method.amount)}</Text>
          </Card>
        ))}
      </Group>

      {/* Filters */}
      <Card>
        <Group>
          <TextInput
            placeholder="Search payments..."
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1 }}
          />
          <Select
            placeholder="Status"
            data={statuses.map(s => ({ value: s, label: s.charAt(0).toUpperCase() + s.slice(1) }))}
            value={statusFilter}
            onChange={setStatusFilter}
            clearable
            w={150}
          />
          <Select
            placeholder="Method"
            data={methods.map(m => ({ value: m, label: getMethodLabel(m as Payment['method']) }))}
            value={methodFilter}
            onChange={setMethodFilter}
            clearable
            w={150}
          />
        </Group>
      </Card>

      {/* Payments Table */}
      <Card>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Invoice</Table.Th>
              <Table.Th>Client</Table.Th>
              <Table.Th>Method</Table.Th>
              <Table.Th>Transaction ID</Table.Th>
              <Table.Th>Date</Table.Th>
              <Table.Th ta="right">Amount</Table.Th>
              <Table.Th>Status</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {sortedPayments.map(payment => {
              const MethodIcon = getMethodIcon(payment.method);
              return (
                <Table.Tr key={payment.id}>
                  <Table.Td>
                    <Text size="sm" fw={500}>{payment.invoiceNumber}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{payment.clientName}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      <MethodIcon size={16} color="var(--mantine-color-dimmed)" />
                      <Text size="sm">{getMethodLabel(payment.method)}</Text>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">{payment.transactionId || '-'}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">{formatDateTime(payment.processedAt)}</Text>
                  </Table.Td>
                  <Table.Td ta="right">
                    <Text size="sm" fw={500} c={payment.status === 'completed' ? 'green' : undefined}>
                      {formatCurrency(payment.amount)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge size="sm" color={getStatusColor(payment.status)}>{payment.status}</Badge>
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </Card>
    </Stack>
  );
}
